import { SagaIterator } from "redux-saga";
import { call, put, SagaGenerator, select, takeEvery } from "typed-redux-saga";

import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { createCustomNotificationAction } from "../../../app/actions";
import { getFVManifest } from "../../../app/slices/fachverfahren/selectors";
import { RESOURCE_KEYS } from "../../../localization";
import { extractStammdaten, isJsonRpc2ResponseArray } from "../../../utilities/rpcHelper";
import { isObject } from "../../../utilities/typeGuardUtils";

import { extractStammdatenAction, openStammdatenAction } from "../actions";

const NOTIFICATION_KEYS = RESOURCE_KEYS.features.stammdaten.notification;

export function* extractStammdatenSaga(): SagaIterator<void> {
	yield* takeEvery(extractStammdatenAction, action => {
		handleExtractStammdaten(
			action.payload.fachverfahrenDocRef,
			action.payload.fachverfahrenId,
			action.payload.activityId
		);
	});
}

export function* handleExtractStammdaten(
	fachverfahrenDocRef: string,
	fachverfahrenId: string,
	activityId: string
): SagaGenerator<void> {
	const fvManifest = yield* select(getFVManifest(fachverfahrenId));
	const mappingConfiguration = fvManifest?.mappingConfigurations?.find(e => e.externalPathSource === "STAMMDATEN");
	if (!(mappingConfiguration && fvManifest)) {
		return;
	}

	try {
		// FIXME: Assertion not good here
		const response = (yield* call(extractStammdaten, fachverfahrenDocRef)) as ExtractStammdatenDto;
		yield* put(
			createCustomNotificationAction({
				severity: "success",
				message: {
					key: NOTIFICATION_KEYS.extract.success.message,
					args: { listname: { type: "plain", value: response.stammdatenListName } }
				},
				title: { key: NOTIFICATION_KEYS.extract.success.title },
				componentId: "NotificationWithButton",
				activityId: activityId,
				button: {
					action: openStammdatenAction({
						docRef: response.docRef,
						initiatingActivityId: activityId
					}),
					label: {
						key: NOTIFICATION_KEYS.extract.success.button.label,
						args: { listname: { type: "plain", value: response.stammdatenListName } }
					}
				},
				group: `extractStammdaten:${fachverfahrenDocRef}`
			})
		);
	} catch (error) {
		yield* call(
			handleError,
			error,
			`extractStammdaten:${fachverfahrenDocRef}`,
			fachverfahrenDocRef,
			fachverfahrenId,
			activityId
		);
	}
}

function* handleError(
	error: any,
	group: string,
	fachverfahrenDocRef: string,
	fachverfahrenId: string,
	activityId: string
): SagaIterator<void> {
	if (
		isJsonRpc2ResponseArray(error) &&
		JsonRpc2Response.JsonRpc2Error.isInstance(error[0].error) &&
		isException(error[0].error.data)
	) {
		if (error[0].error.data.description.key === "MISSING_STAMMDATEN_DEPLOYMENT") {
			yield* put(
				createCustomNotificationAction({
					severity: "warning",
					message: {
						key: NOTIFICATION_KEYS.extract.warning.message
					},
					title: { key: NOTIFICATION_KEYS.extract.warning.title },
					activityId: activityId,
					group
				})
			);
			return;
		}
	}
	yield* put(
		createCustomNotificationAction({
			severity: "error",
			message: {
				key: NOTIFICATION_KEYS.extract.error.message,
				args: { listname: { type: "plain", value: "" } }
			},
			title: { key: NOTIFICATION_KEYS.extract.error.title },
			componentId: "NotificationWithButton",
			activityId: activityId,
			button: {
				action: extractStammdatenAction({ fachverfahrenDocRef, fachverfahrenId, activityId }),
				label: { key: NOTIFICATION_KEYS.extract.error.button.label }
			},
			group
		})
	);
}

function isException(data: unknown): data is JsonRpc2Response.Exception {
	return (
		isObject(data) &&
		"level" in data &&
		"description" in data &&
		JsonRpc2Response.LocalizableMessage.isInstance(data.description)
	);
}

interface ExtractStammdatenDto {
	readonly docRef: string;
	readonly stammdatenListName: string;
}
